import { useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { STAY_PROPERTY_TYPES } from "@majourneys/shared";
import { Icon } from "@majourneys/icons";
import { openHubFilters, useHubFiltersCount } from "../../activities/components/hub-dialogs";
import { useStayTypeCounts } from "../api/stays-query";
import { STAY_VIBES, VIBE_ICON, type StayVibe } from "../lib/vibes";
import { STAYS_EASE, prefersReducedMotion } from "../lib/motion";
import { StaysHubMapLazy, type StaysMapPin, type StaysMapView } from "./stays-hub-map-lazy";
import { openStaySearch } from "./stay-search-pill";
import { MoodGlyph } from "./mood-glyph";
import { SaveHeart, type SaveTarget } from "../../saved/components/save-heart";
import { CONTROL_EDGE, CONTROL_LIT, PEWTER_GAP, pewterEdge, pewterFrame, pewterInner } from "../../../shared/ui/pewter-edge";
import { PANEL_FILL } from "../lib/stay-panel";
import { BEVEL_CHIP_ON, BEVEL_INSET_NAVY, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";

const NAVY = "#110E39";
const META = "#5a5d77";
/* Pewter house rules: the band is for the surface you read (the sheet); a
   control at rest is lit white with one hairline, and navy when on. */
const CONTROL = { background: CONTROL_LIT, color: NAVY, boxShadow: `inset 0 0 0 1px ${CONTROL_EDGE}` } as const;
/** A chip or button at rest: the soft-bevel white face over the same hairline. The search box keeps CONTROL: it is a field. */
const CONTROL_BEVEL = { background: BEVEL_WHITE_FACE, color: NAVY, boxShadow: `${BEVEL_INSET_WHITE}, inset 0 0 0 1px ${CONTROL_EDGE}` } as const;
const CHIP_ON = { background: BEVEL_NAVY_FACE, color: "#fff", boxShadow: BEVEL_CHIP_ON } as const;

/** A stay as the phone map's card needs it. */
export interface PhoneMapStay {
  id: string;
  name: string;
  image?: string;
  href: string;
  search?: Record<string, unknown>;
  typeLabel?: string;
  stars?: number;
  city?: string;
  /** The heart's target: camps save as activities, the rest as stays. */
  save?: SaveTarget;
  nearby?: string | null;
  price?: string;
  lat: number;
  lng: number;
}

export interface StaysPhoneMapProps {
  lang: string;
  onClose: () => void;
  pins: StaysMapPin[];
  landmarks?: StaysMapPin[];
  stays: Map<string, PhoneMapStay>;
  fitKey: string;
  count: number;
  /** "Marrakech · Any dates": what the search pill says. */
  searchSummary: string;
  activeType: string;
  carriedSearch?: Record<string, unknown>;
  vibes: StayVibe[];
  vibeCounts: Record<StayVibe, number>;
  onVibesChange: (next: StayVibe[]) => void;
  /** Dates picked: prices are real, so the "add dates" prompt steps aside. */
  datesSelected: boolean;
  /** Shown instead of the list (and the count) while stays are being priced. */
  searching?: ReactNode;
}

/**
 * Where the sheet rests, as in the app's Discover screen: a peek with just the
 * controls, half the phone (where it opens) with the first stays showing, and
 * nearly full, under the round map buttons, where the list scrolls. A picked
 * stay's card has its own height.
 */
export type Snap = "peek" | "mid" | "full";
const SNAPS: Snap[] = ["peek", "mid", "full"];
const PEEK_SHARE = 0.3;
const MID_SHARE = 0.52;
const CARD_SHARE = 0.36;
/** Room left above the full sheet for the ✕ and focus buttons. */
const FULL_TOP = 76;
/** The phone's Back button closes the map: it gets its own history entry. */
const LAYER_KEY = "mjStaysMap";

/**
 * Where the guest left the map: camera, sheet height and picked stay, for
 * this exact page and search. "See rooms" and Back return to it as it was;
 * closing the map on purpose (✕, Back on the map) forgets it.
 */
const SAVED_KEY = "mj-stays-phone-map";
interface SavedMap {
  href: string;
  lat: number;
  lng: number;
  zoom: number;
  snap: Snap;
  pickedId: string | null;
}
const hereHref = () => window.location.pathname + window.location.search;
function readSaved(): SavedMap | null {
  try {
    const v = JSON.parse(window.sessionStorage.getItem(SAVED_KEY) ?? "null") as SavedMap | null;
    return v && v.href === hereHref() ? v : null;
  } catch {
    return null;
  }
}
function writeSaved(v: SavedMap | null) {
  try {
    if (v) window.sessionStorage.setItem(SAVED_KEY, JSON.stringify(v));
    else window.sessionStorage.removeItem(SAVED_KEY);
  } catch {
    /* Storage blocked: the map simply opens fresh. */
  }
}

/**
 * The phone's map view on the Stays pages (owner, 2026-09-28), in the style of
 * the app's Discover screen: the map edge to edge, 3D type icons with price
 * pills, navy bubbles with gold numbers, round map buttons, and one bottom
 * sheet holding the types, search, Filters and moods.
 *
 * A layer over the list, never a page of its own: every choice is the same
 * state the list reads, so a mood picked here is on when the map closes, and
 * the list keeps its scroll position underneath. It closes on ✕ or the
 * phone's Back button. Pulling the sheet up browses the stays inside it, as
 * the app does (owner, 2026-09-29: pulling up must not leave the map).
 *
 * Tapping a pin turns the sheet into that stay's card; swiping the card
 * steps to the next stay nearby and the map follows.
 */
export function StaysPhoneMap(props: StaysPhoneMapProps) {
  const { t } = useTranslation();
  return (
    <PhoneMap
      label={t("stays.hub_map.label")}
      onClose={props.onClose}
      pins={props.pins}
      landmarks={props.landmarks}
      items={props.stays}
      fitKey={props.fitKey}
      searching={props.searching}
      cta={t("stays.phone_map.see_rooms")}
      swipeHint={t("stays.phone_map.swipe_next")}
      priceSuffix={t("stays.per_night")}
      controls={(api) => <StaysSheetControls {...props} api={api} />}
    />
  );
}

/** What the sheet's controls can ask of the map layer around them. */
export interface PhoneMapApi {
  /** Slide the layer away (✕). */
  close: () => void;
  /** Frame every pin again. */
  showAll: () => void;
  /** How many of the items are inside the visible part of the map. */
  inView: number;
  /** The map is zoomed in on part of the list. */
  narrowed: boolean;
  /** Raise or lower the sheet. */
  setSnap: (snap: Snap) => void;
}

export interface PhoneMapProps {
  label: string;
  onClose: () => void;
  pins: StaysMapPin[];
  landmarks?: StaysMapPin[];
  items: Map<string, PhoneMapStay>;
  fitKey: string;
  /** The sheet's top: types, search, Filters, chips and the count. */
  controls: (api: PhoneMapApi) => ReactNode;
  /** Shown instead of the list (and the count) while the list is loading. */
  searching?: ReactNode;
  /** Shown in the sheet instead of the list, e.g. a city picker. */
  body?: ReactNode;
  /** The picked card's button ("See rooms"). */
  cta: string;
  swipeHint: string;
  /** After a price: "per night". */
  priceSuffix?: string;
  /** "discover": 3D type icons (Stays). "photo": the venue's photo (the hubs). */
  pinVariant?: "discover" | "photo";
}

/**
 * The full-screen phone map, as on Stays: the map edge to edge, round ✕ and
 * focus buttons, and one bottom sheet holding the page's controls and the
 * places in view. Stays and the category hubs share it (owner, 2026-10-04).
 */
export function PhoneMap(props: PhoneMapProps) {
  if (typeof document === "undefined") return null;
  return createPortal(<PhoneMapLayer {...props} />, document.body);
}

function PhoneMapLayer({
  label,
  onClose,
  pins,
  landmarks,
  items: stays,
  fitKey,
  controls,
  searching,
  body,
  cta,
  swipeHint,
  priceSuffix,
  pinVariant = "discover",
}: PhoneMapProps) {
  const { t } = useTranslation();
  // Read once, on opening: a map left for a stay page comes back as it was.
  const [saved] = useState(() => (typeof window === "undefined" ? null : readSaved()));
  const [pickedId, setPickedId] = useState<string | null>(() => (saved?.pickedId && stays.has(saved.pickedId) ? saved.pickedId : null));
  const [snap, setSnap] = useState<Snap>(saved?.snap ?? "mid");
  const [view, setView] = useState<StaysMapView | null>(null);
  const [fitSignal, setFitSignal] = useState(0);
  // A stay tapped in the list: the map glides to it.
  const [focus, setFocus] = useState<{ lat: number; lng: number; key: number } | null>(null);
  const [entered, setEntered] = useState(prefersReducedMotion());
  const picked = pickedId ? stays.get(pickedId) ?? null : null;

  /* Slide up on open; the page underneath stops scrolling while it is up. */
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    const html = document.documentElement;
    const was = html.style.overflow;
    html.style.overflow = "hidden";
    return () => {
      cancelAnimationFrame(id);
      html.style.overflow = was;
    };
  }, []);

  /* Back closes the map. The layer adds one history entry of its own,
     carrying the router's state along so the router sees the same page.
     Coming back to an entry that already has it (Back from a hotel, or a
     reload with the map remembered) reuses that entry instead of stacking
     a second one. */
  const pushed = useRef(false);
  const done = useRef(false);
  const selfClosing = useRef(false);
  /* Leaving slides the layer away the way it came (✕, Back, search). */
  const [leaving, setLeaving] = useState(false);
  const LEAVE_MS = prefersReducedMotion() ? 120 : 320;
  const finish = () => {
    if (done.current) return;
    done.current = true;
    writeSaved(null);
    onClose();
  };
  useEffect(() => {
    if (!window.history.state?.[LAYER_KEY]) {
      window.history.pushState({ ...(window.history.state ?? {}), [LAYER_KEY]: true }, "");
      pushed.current = true;
    }
    // The phone's own Back: slide away, then close.
    const onPop = () => {
      if (selfClosing.current || done.current) return;
      setLeaving(true);
      window.setTimeout(finish, LEAVE_MS);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // Once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /* ✕ and search: the history step and the exit motion run together,
     and the layer closes once both are over, so the router's own Back
     handling lands while the page underneath is still hidden and still. */
  const close = () => {
    if (selfClosing.current || done.current) return;
    selfClosing.current = true;
    setLeaving(true);
    if (pushed.current && window.history.state?.[LAYER_KEY]) {
      window.history.back();
    } else {
      const { [LAYER_KEY]: _drop, ...rest } = window.history.state ?? {};
      window.history.replaceState(rest, "");
    }
    window.setTimeout(finish, LEAVE_MS);
  };

  /* Remember where the guest is, for "See rooms" and back. Not after a
     deliberate close: finish() has just forgotten it. */
  useEffect(() => {
    if (!view || done.current) return;
    writeSaved({ href: hereHref(), lat: view.lat, lng: view.lng, zoom: view.zoom, snap, pickedId });
  }, [view, snap, pickedId]);

  /* The list follows the map: only the stays in view, like the app. Framing
     everything puts every stay in view, so the full list is the default. */
  const all = useMemo(() => [...stays.values()], [stays]);
  // Only the map the guest can see: the part under the sheet does not count.
  // At full height the sheet covers the map, so the half-height view stands.
  const visibleShare = snap === "peek" ? 1 - PEEK_SHARE : 1 - MID_SHARE;
  const inView = useMemo(() => {
    if (!view) return all;
    const b = view.bounds;
    const south = view.latAt(visibleShare);
    return all.filter((x) => x.lat <= b.north && x.lat >= south && x.lng <= b.east && x.lng >= b.west);
  }, [all, view, visibleShare]);
  const narrowed = inView.length < all.length;

  /* The swipe walks outward from the tapped stay, nearest first. */
  const order = useRef<string[]>([]);
  const nearest = useMemo(() => {
    if (!picked) return [];
    return [...stays.values()].sort(
      (a, b) => Math.hypot(a.lat - picked.lat, a.lng - picked.lng) - Math.hypot(b.lat - picked.lat, b.lng - picked.lng),
    );
  }, [stays, picked]);
  function pick(id: string | null) {
    setPickedId(id);
    if (!id) order.current = [];
    else if (!order.current.includes(id)) order.current = [id];
  }
  function step(dir: 1 | -1) {
    if (!picked) return;
    const i = order.current.indexOf(picked.id);
    if (dir === 1) {
      const next = nearest.find((s) => !order.current.includes(s.id));
      if (next) {
        order.current.push(next.id);
        setPickedId(next.id);
      }
    } else if (i > 0) {
      const prev = order.current[i - 1]!;
      order.current = order.current.slice(0, i);
      setPickedId(prev);
    }
  }

  const round = "absolute z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white transition-transform active:scale-[0.94]";
  const roundStyle = { color: NAVY, boxShadow: "0 8px 20px -8px rgba(17,14,57,0.45)" } as const;

  // overflow-clip, not hidden: a hidden box can still be scrolled by the
  // browser (focusing a chip in the sheet slid the whole layer up by a third).
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      className="fixed inset-0 z-[5000] overflow-clip bg-[#e9ebf2] lg:hidden"
      style={{
        transform: entered && !leaving ? "none" : "translateY(100%)",
        transition: `transform ${leaving ? LEAVE_MS : 340}ms ${STAYS_EASE}`,
      }}
    >
      <StaysHubMapLazy
        bare
        variant={pinVariant}
        pins={pins}
        landmarks={landmarks}
        fitKey={fitKey}
        fitSignal={fitSignal}
        // Framed above the half-height sheet the map opens with.
        fitPadding={{ top: 80, left: 30, right: 30, bottom: Math.round((typeof window === "undefined" ? 800 : window.innerHeight) * MID_SHARE) + 16 }}
        activeId={pickedId}
        focus={focus}
        onIdle={setView}
        initialCamera={saved ? { lat: saved.lat, lng: saved.lng, zoom: saved.zoom } : null}
        focusOffset={Math.round((typeof window === "undefined" ? 800 : window.innerHeight) * CARD_SHARE * 0.5)}
        onHover={() => {}}
        onSelect={pick}
        className="absolute inset-0 h-full"
      />

      <button type="button" aria-label={t("stays.hub_map.close")} onClick={close} className={round} style={{ ...roundStyle, left: 14, top: "max(14px, env(safe-area-inset-top))" }}>
        <Icon name="ri-close-line" className="text-[22px]" aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label={t("stays.hub_map.show_all")}
        onClick={() => setFitSignal((n) => n + 1)}
        className={round}
        style={{ ...roundStyle, right: 14, top: "max(14px, env(safe-area-inset-top))" }}
      >
        <Icon name="ri-focus-3-line" className="text-[20px]" aria-hidden="true" />
      </button>

      <PhoneSheet
        picked={picked}
        onUnpick={() => pick(null)}
        onStep={step}
        list={inView}
        searching={searching}
        body={body}
        cta={cta}
        swipeHint={swipeHint}
        priceSuffix={priceSuffix}
        snap={snap}
        onSnap={setSnap}
        onPickFromList={(stay) => {
          pick(stay.id);
          setFocus({ lat: stay.lat, lng: stay.lng, key: Date.now() });
        }}
      >
        {controls({ close, showAll: () => setFitSignal((n) => n + 1), inView: inView.length, narrowed, setSnap })}
      </PhoneSheet>
    </div>
  );
}


/** The Stays sheet's controls: types, the search pill, Filters, moods, dates. */
function StaysSheetControls({
  lang,
  count,
  searchSummary,
  activeType,
  carriedSearch = {},
  vibes,
  vibeCounts,
  onVibesChange,
  datesSelected,
  searching,
  api,
}: StaysPhoneMapProps & { api: PhoneMapApi }) {
  const { t } = useTranslation();
  const filterCount = useHubFiltersCount();
  const counts = useStayTypeCounts(true);
  const types = [
    { slug: "stays", label: t("common.all") },
    // Plural and right after "All", as in the app.
    { slug: "camp", label: t("stays.camps") },
    ...STAY_PROPERTY_TYPES.filter((type) => (counts?.get(type) ?? 1) > 0)
      .sort((a, b) => (counts?.get(b) ?? 0) - (counts?.get(a) ?? 0))
      .map((type) => ({ slug: type, label: t(`stays.types.${type}`) })),
  ];
  return (
    <>
      {/* Types: the same row the list page carries, as links. */}
      <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-3 pt-1 [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:hidden">
        {types.map((type) => {
          const on = type.slug === activeType;
          return (
            <Link
              key={type.slug}
              to="/$lang/category/$category"
              params={{ lang, category: type.slug }}
              search={carriedSearch as never}
              resetScroll={false}
              aria-current={on ? "page" : undefined}
              className="inline-flex h-9 shrink-0 items-center rounded-full px-3.5 text-[13px] font-semibold no-underline"
              style={on ? CHIP_ON : CONTROL_BEVEL}
            >
              {type.label}
            </Link>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            // The page's own search opens above the map; the map stays.
            if (openStaySearch()) return;
            api.close();
            window.setTimeout(() => window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior }), 120);
          }}
          className="flex min-h-12 py-1.5 min-w-0 flex-1 items-center gap-2 rounded-full px-4 text-start text-[14px]"
          style={{ ...CONTROL, color: META }}
        >
          <Icon name="ri-search-line" className="shrink-0 text-[17px]" aria-hidden="true" />
          <span className="min-w-0 break-words leading-tight">{searchSummary}</span>
        </button>
        <button
          type="button"
          aria-label={t("filter.filters", { defaultValue: "Filters" })}
          onClick={openHubFilters}
          className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
          style={CONTROL_BEVEL}
        >
          <Icon name="ri-equalizer-line" className="text-[18px]" aria-hidden="true" />
          {filterCount ? (
            <span className="absolute -end-0.5 -top-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-bold text-white" style={{ background: NAVY }}>
              {filterCount}
            </span>
          ) : null}
        </button>
      </div>
      <div className="-mx-4 mt-3 flex gap-2 overflow-x-auto px-4 pb-3 pt-1 [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:hidden">
        {STAY_VIBES.map((v) => {
          const on = vibes.includes(v);
          if (!on && vibeCounts[v] === 0) return null;
          return (
            <button
              key={v}
              type="button"
              aria-pressed={on}
              onClick={() => onVibesChange(on ? vibes.filter((x) => x !== v) : [...vibes, v])}
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-semibold transition-[transform,background,color] duration-150 active:scale-[0.97]"
              style={on ? CHIP_ON : CONTROL_BEVEL}
            >
              <MoodGlyph icon={VIBE_ICON[v]} on={on} size={14} />
              {t(`stays.vibes_short.${v}`)}
            </button>
          );
        })}
      </div>
      {datesSelected ? null : (
        // Most stays have no price until dates are set: lead there.
        <button
          type="button"
          onClick={() => openStaySearch("when")}
          className="mb-3 flex min-h-11 py-2 w-full items-center gap-2.5 rounded-2xl px-3.5 text-start text-[13px] font-semibold transition-transform active:scale-[0.99]"
          style={CONTROL_BEVEL}
        >
          <Icon name="ri-calendar-line" className="shrink-0 text-[17px]" aria-hidden="true" />
          <span className="min-w-0 flex-1 break-words">{t("stays.phone_map.add_dates")}</span>
          <Icon name="ri-arrow-right-s-line" className="shrink-0 text-[18px] rtl:rotate-180" style={{ color: META }} aria-hidden="true" />
        </button>
      )}
      {searching ? null : (
      <p className="m-0 flex items-center justify-center gap-1.5 text-[12.5px] font-semibold" style={{ color: META }}>
        {api.narrowed ? t("stays.phone_map.count_area", { count: api.inView }) : t("stays.phone_map.count", { count })}
        {api.narrowed ? (
          <>
            <span aria-hidden="true">·</span>
            <button type="button" onClick={api.showAll} className="font-bold underline underline-offset-2" style={{ color: NAVY }}>
              {t("stays.hub_map.show_all")}
            </button>
          </>
        ) : null}
      </p>
      )}
    </>
  );
}

/**
 * The bottom sheet, after the app's Discover screen. It rests at a peek (the
 * controls), half the phone (the controls and the first stays) or nearly full
 * (the stays scroll). Drag it anywhere, including from a chip or a button: a
 * tap is still a tap, a drag moves the sheet. With a stay picked it holds that
 * stay's card; dragging the card down goes back to the list.
 */
function PhoneSheet({
  picked,
  onUnpick,
  onStep,
  list,
  searching,
  body,
  cta,
  swipeHint,
  priceSuffix,
  onPickFromList,
  snap,
  onSnap: setSnap,
  children,
}: {
  body?: ReactNode;
  cta: string;
  swipeHint: string;
  priceSuffix?: string;
  snap: Snap;
  onSnap: (s: Snap) => void;
  picked: PhoneMapStay | null;
  onUnpick: () => void;
  onStep: (dir: 1 | -1) => void;
  list: PhoneMapStay[];
  searching?: ReactNode;
  onPickFromList: (stay: PhoneMapStay) => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [h, setH] = useState(() => (typeof window === "undefined" ? 800 : window.innerHeight));
  useEffect(() => {
    const onResize = () => setH(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const drag = useRef<{ x0: number; y0: number; t0: number; id: number; axis: "x" | "y" | null } | null>(null);
  const dragged = useRef(false);
  const [dy, setDy] = useState(0);

  // A picked stay's card: the sheet hugs it, with no empty band below.
  const headRef = useRef<HTMLDivElement>(null);
  const [headH, setHeadH] = useState(0);
  useEffect(() => {
    const el = headRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setHeadH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const cardTop = headH ? Math.max(FULL_TOP, h - headH - 8) : h * (1 - CARD_SHARE);

  const topOf = (s: Snap) => (s === "full" ? FULL_TOP : s === "mid" ? h * (1 - MID_SHARE) : h * (1 - PEEK_SHARE));
  const top = picked ? cardTop : topOf(snap);
  const full = !picked && snap === "full";

  // The list starts from its top whenever the sheet leaves full height.
  useEffect(() => {
    if (!full && listRef.current) listRef.current.scrollTop = 0;
  }, [full]);

  /* Let go: a flick goes one rest further that way, a slow drag settles on
     the nearest rest. With a card, down closes it and up opens the list. */
  function settle(d: number, v: number) {
    if (picked) {
      if (d > 60 || v > 0.6) onUnpick();
      else if (d < -60 || v < -0.6) {
        onUnpick();
        setSnap("mid");
      }
      return;
    }
    const i = SNAPS.indexOf(snap);
    if (v < -0.5) return setSnap(SNAPS[Math.min(2, i + 1)]!);
    if (v > 0.5) return setSnap(SNAPS[Math.max(0, i - 1)]!);
    const y = topOf(snap) + d;
    setSnap(SNAPS.reduce((best, s) => (Math.abs(topOf(s) - y) < Math.abs(topOf(best) - y) ? s : best), snap));
  }

  /* The drag is followed on the window once it starts: a quick flick's first
     move can already be over the map. At full height the list scrolls itself,
     so a drag that starts on it is left to the browser. */
  function onDown(e: RPointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    if (full && listRef.current?.contains(e.target as Node)) return;
    const g = { x0: e.clientX, y0: e.clientY, t0: performance.now(), id: e.pointerId, axis: null as "x" | "y" | null };
    drag.current = g;
    dragged.current = false;
    const move = (ev: PointerEvent) => {
      if (ev.pointerId !== g.id) return;
      const dx = ev.clientX - g.x0;
      const d = ev.clientY - g.y0;
      if (!g.axis) {
        if (Math.abs(dx) < 6 && Math.abs(d) < 6) return;
        g.axis = Math.abs(d) > Math.abs(dx) ? "y" : "x";
        if (g.axis === "y") {
          dragged.current = true;
          window.getSelection()?.removeAllRanges();
        }
      }
      if (g.axis !== "y") return;
      ev.preventDefault();
      // Firm past the ends: above full, below the peek.
      const y = top + d;
      const lo = FULL_TOP;
      const hi = picked ? cardTop : topOf("peek");
      setDy(y < lo ? lo - top + (y - lo) * 0.3 : y > hi ? hi - top + (y - hi) * 0.4 : d);
    };
    const end = (ev: PointerEvent) => {
      if (ev.pointerId !== g.id) return;
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", end);
      window.removeEventListener("pointercancel", end);
      drag.current = null;
      if (g.axis !== "y") return;
      setDy(0);
      if (ev.type === "pointercancel") return;
      const d = ev.clientY - g.y0;
      settle(d, d / Math.max(1, performance.now() - g.t0));
    };
    window.addEventListener("pointermove", move, { passive: false });
    window.addEventListener("pointerup", end);
    window.addEventListener("pointercancel", end);
  }

  /* Trackpad and mouse wheel step the sheet between its rests; at full
     height the list scrolls, and scrolling back past its top lowers the sheet. */
  const wheel = useRef({ sum: 0, at: 0 });
  const live = useRef({ snap, picked, full });
  live.current = { snap, picked, full };
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      const { snap: s, picked: p, full: f } = live.current;
      const lst = listRef.current;
      if (f && lst?.contains(e.target as Node) && (e.deltaY > 0 || lst.scrollTop > 0)) return;
      e.preventDefault();
      const w = wheel.current;
      const now = performance.now();
      if (now - w.at > 250) w.sum = 0;
      w.at = now;
      w.sum += e.deltaY;
      if (Math.abs(w.sum) < 60) return;
      const up = w.sum > 0;
      w.sum = up ? -Infinity : Infinity;
      if (p) {
        onUnpick();
        if (up) setSnap("mid");
        return;
      }
      const i = SNAPS.indexOf(s);
      setSnap(SNAPS[up ? Math.min(2, i + 1) : Math.max(0, i - 1)]!);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onUnpick]);

  return (
    <div
      ref={ref}
      onPointerDown={onDown}
      // A drag that started on a chip or a button must not also press it.
      onClickCapture={(e) => {
        if (!dragged.current) return;
        dragged.current = false;
        e.preventDefault();
        e.stopPropagation();
      }}
      // The sheet is a reading surface: the pale pewter band around near-white,
      // at the house 4px. It floats 10px in from the screen's sides so the band
      // never runs into the edge (owner, 2026-10-11). Its bottom never shows:
      // the sheet is as tall as the phone and always sits lower than its top,
      // and lifting it would mean animating its height between the snaps.
      className="absolute inset-x-2.5 top-0 z-20 flex h-full min-w-0 select-none flex-col"
      style={{
        ...pewterEdge(PANEL_FILL, 26, { variant: "matte", width: PEWTER_GAP }),
        transform: `translateY(${top + dy}px)`,
        transition: drag.current?.axis === "y" ? "none" : `transform 340ms ${STAYS_EASE}`,
        boxShadow: "0 -10px 28px -12px rgba(17,14,57,0.35)",
        touchAction: "none",
      }}
    >
      {/* A finger aimed at the handle often lands just above the sheet, on
          the map: this strip hands those touches to the sheet instead. */}
      <div className="absolute inset-x-0 -top-5 h-5" aria-hidden="true" />
      <div ref={headRef} className="shrink-0 px-4 pb-[max(8px,env(safe-area-inset-bottom))] pt-3">
        <div className="mx-auto mb-3 h-[5px] w-12 rounded-full bg-[#c9ccd8]" aria-hidden="true" />
        {picked ? <PickedCard stay={picked} onStep={onStep} swipeHint={swipeHint} cta={cta} priceSuffix={priceSuffix} /> : children}
      </div>
      {picked ? null : (
        <div
          ref={listRef}
          className="min-h-0 flex-1 px-4 pt-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{
            overflowY: full ? "auto" : "hidden",
            overscrollBehavior: "contain",
            touchAction: full ? "pan-y" : "none",
            // The sheet is as tall as the phone but sits FULL_TOP down, so
            // the last row needs that much room to scroll clear of the edge.
            paddingBottom: `calc(${FULL_TOP + 24}px + env(safe-area-inset-bottom))`,
          }}
        >
          {body ?? (
            <>
              {searching ? searching : null}
              <ul className="m-0 grid list-none grid-cols-2 gap-x-3 gap-y-5 p-0">
                {list.map((stay) => (
                  <li key={stay.id} className="min-w-0">
                    <SheetStayCard stay={stay} priceSuffix={priceSuffix} onPick={() => onPickFromList(stay)} />
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* The photo's frame: the thin lit pewter ring that image cards wear (the
   band is for reading surfaces, like the sheet around it). */
const PHOTO_RADIUS = 20;
const PHOTO_FRAME = (() => {
  const f = pewterFrame(PHOTO_RADIUS);
  return { ...f, boxShadow: `${f.boxShadow}, 0 6px 18px -8px rgba(17,14,57,0.22)` };
})();
const PHOTO_INNER = pewterInner("#eef0f5", PHOTO_RADIUS);

/**
 * A stay in the sheet's list, in the style of the explore cards: the photo in
 * its pewter frame, then the name and "Type · City" under it, and the price
 * when there is one. Tapping it shows the stay on the map.
 */
function SheetStayCard({ stay, priceSuffix, onPick }: { stay: PhoneMapStay; priceSuffix?: string; onPick: () => void }) {
  const tags = [stay.typeLabel, stay.city].filter(Boolean).join(" · ");
  return (
    <div className="relative min-w-0">
    <button type="button" onClick={onPick} className="block w-full min-w-0 text-start">
      <span className="block" style={PHOTO_FRAME}>
        <span className="block aspect-square overflow-hidden" style={PHOTO_INNER}>
          {stay.image ? (
            <img src={stay.image} alt="" loading="lazy" draggable={false} className="h-full w-full object-cover" />
          ) : null}
        </span>
      </span>
      <span className="mt-2.5 line-clamp-2 break-words px-0.5 text-[15px] font-bold leading-tight" style={{ color: NAVY }}>
        {stay.name}
      </span>
      {tags ? (
        <span className="mt-1 line-clamp-2 px-0.5 text-[13px]" style={{ color: META }}>
          {tags}
        </span>
      ) : null}
      {stay.price ? (
        <span className="mt-0.5 block break-words px-0.5 text-[13px] font-bold" style={{ color: NAVY }}>
          {stay.price} {priceSuffix ? <span className="font-medium" style={{ color: META }}>{priceSuffix}</span> : null}
        </span>
      ) : null}
    </button>
    {/* Beside the card's button, not in it: a button inside a button is not
        valid, and the heart must save without also picking the stay. */}
    {stay.save ? <SaveHeart variant="bare" target={stay.save} className="absolute end-1.5 top-1.5" /> : null}
    </div>
  );
}

function PickedCard({
  stay,
  onStep,
  swipeHint,
  cta,
  priceSuffix,
}: {
  stay: PhoneMapStay;
  onStep: (dir: 1 | -1) => void;
  swipeHint: string;
  cta: string;
  priceSuffix?: string;
}) {
  const x0 = useRef<{ x: number; y: number; axis: "x" | "y" | null } | null>(null);
  const [dx, setDx] = useState(0);
  return (
    <div
      onPointerDown={(e) => {
        x0.current = { x: e.clientX, y: e.clientY, axis: null };
      }}
      onPointerMove={(e) => {
        const g = x0.current;
        if (!g) return;
        const mx = e.clientX - g.x;
        const my = e.clientY - g.y;
        if (!g.axis) {
          if (Math.abs(mx) < 6 && Math.abs(my) < 6) return;
          g.axis = Math.abs(mx) > Math.abs(my) ? "x" : "y";
        }
        if (g.axis === "x") setDx(mx);
      }}
      onPointerUp={() => {
        if (dx < -50) onStep(1);
        else if (dx > 50) onStep(-1);
        x0.current = null;
        setDx(0);
      }}
      onPointerCancel={() => {
        x0.current = null;
        setDx(0);
      }}
      style={{
        transform: `translateX(${dx}px)`,
        transition: x0.current === null ? `transform 260ms ${STAYS_EASE}` : "none",
      }}
    >
      <div className="flex items-center gap-3">
        {stay.image ? <img src={stay.image} alt="" draggable={false} className="h-[84px] w-[84px] shrink-0 rounded-2xl object-cover" /> : null}
        <div className="min-w-0 flex-1">
          <p className="m-0 line-clamp-2 break-words text-[15px] font-bold" style={{ color: NAVY }}>{stay.name}</p>
          <p className="m-0 mt-0.5 text-[12px]" style={{ color: META }}>
            {[stay.typeLabel, stay.stars ? "★".repeat(stay.stars) : null].filter(Boolean).join(" · ")}
          </p>
          {stay.nearby ? <p className="m-0 mt-0.5 line-clamp-2 text-[12px]" style={{ color: META }}>{stay.nearby}</p> : null}
          {stay.price ? (
            <p className="m-0 mt-1 text-[14px] font-bold" style={{ color: NAVY }}>
              {stay.price} {priceSuffix ? <span className="font-medium" style={{ color: META }}>{priceSuffix}</span> : null}
            </p>
          ) : null}
        </div>
      </div>
      <Link
        to={stay.href as never}
        search={(stay.search ?? {}) as never}
        className="mt-3 flex h-12 items-center justify-center rounded-full text-[14px] font-semibold text-white no-underline"
        style={{ background: BEVEL_NAVY_FACE, boxShadow: BEVEL_INSET_NAVY }}
      >
        {cta}
      </Link>
      <p className="m-0 mt-2 text-center text-[11.5px]" style={{ color: META }}>
        {swipeHint}
      </p>
    </div>
  );
}
